import {utils} from '@wallaby-ui/core';

import {CSS_UNITS, DEFAULT_SCALE} from './pdf_viewer_constants';
import pdf from './pdfjs_loader';
import {RenderingStates} from './render_queue';
import {LIBRARY_NAME} from 'src/js/common/constants';

const logger = utils.LogUtils.getLogger('PageView', LIBRARY_NAME);
const TEXT_LAYER_RENDER_DELAY = 200; // ms

// utils
function roundToDivide(x, div) {
  const r = x % div;
  return r === 0 ? x : Math.round(x - r + div);
}

export class PDFPageView {
  constructor(options) {
    const {
      container,
      id,
      scale,
      defaultViewport,
      renderingQueue,
      textLayerFactory,
      TCLayerBuilder,
    } = options;

    this.id = id;
    this.renderingId = `page${id}`;
    this.rotation = 0;
    this.scale = scale || DEFAULT_SCALE;
    this.viewport = defaultViewport;
    this.pdfPageRotate = defaultViewport.rotation;
    this.hasRestrictedScaling = false;
    this.eventBus = options.eventBus;
    this.renderingQueue = renderingQueue;
    this.textLayerFactory = textLayerFactory;
    this.TCLayerBuilder = TCLayerBuilder;
    this.renderingState = RenderingStates.INITIAL;
    this.resume = null;
    this.onBeforeDraw = null;
    this.onAfterDraw = null;
    this.textLayer = null;
    this.zoomLayer = null;
    this.TCLayer = null;

    const div = document.createElement('div');
    div.id = `pageContainer${this.id}`;
    div.className = 'page';
    div.style.width = `${Math.floor(this.viewport.width)}px`;
    div.style.height = `${Math.floor(this.viewport.height)}px`;
    div.setAttribute('data-page-number', this.id);
    this.div = div;

    container.appendChild(div);
  }

  /**
   * Returns scale factor for the canvas.
   */
  static getOutputScale(ctx) {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const backingStoreRatio =
      ctx.webkitBackingStorePixelRatio ||
      ctx.mozBackingStorePixelRatio ||
      ctx.msBackingStorePixelRatio ||
      ctx.oBackingStorePixelRatio ||
      ctx.backingStorePixelRatio ||
      1;
    const pixelRatio = devicePixelRatio / backingStoreRatio;
    return {
      sx: pixelRatio,
      sy: pixelRatio,
      scaled: pixelRatio !== 1,
    };
  }

  setPdfPage(pdfPage) {
    this.pdfPage = pdfPage;
    this.pdfPageRotate = pdfPage.rotate;
    const totalRotation = (this.rotation + this.pdfPageRotate) % 360;
    this.viewport = pdfPage.getViewport({
      scale: this.scale * CSS_UNITS,
      rotation: totalRotation,
    });
    this.stats = pdfPage.stats;
    this.reset();
  }

  destroy() {
    this.zoomLayer = null;
    this.reset();
    if (this.pdfPage) {
      this.pdfPage.cleanup();
    }
  }

  reset(keepZoomLayer, keepTC) {
    if (this.renderTask) {
      this.renderTask.cancel();
    }
    this.resume = null;
    this.renderingState = RenderingStates.INITIAL;

    this.div.style.width = `${Math.floor(this.viewport.width)}px'`;
    this.div.style.height = `${Math.floor(this.viewport.height)}px`;

    const {childNodes} = this.div;
    const currentZoomLayerNode = (keepZoomLayer && this.zoomLayer) || null;
    const currentTCNode = (keepTC && this.TCLayer && this.TCLayer.div) || null;
    for (let i = childNodes.length - 1; i >= 0; i--) {
      const node = childNodes[i];
      if (currentZoomLayerNode === node || currentTCNode === node) {
        // eslint-disable-next-line no-continue
        continue;
      }
      this.div.removeChild(node);
    }
    this.div.removeAttribute('data-loaded');

    if (currentTCNode) {
      // hide layer until all elements are resized
      // so they are not displayed on the already-resized page
      this.TCLayer.hide();
    } else {
      this.TCLayer = null;
    }

    if (this.canvas && !currentZoomLayerNode) {
      // Zeroing the width and height causes Firefox to release graphics
      // resources immediately, which can greatly reduce memory consumption.
      this.canvas.width = 0;
      this.canvas.height = 0;
      delete this.canvas;
    }

    this.loadingIconDiv = document.createElement('div');
    this.spinner = document.createElement('div');
    this.spinner.className = 'text-center';
    this.spinner.innerHTML =
      '<i class="fas fa-cog fa-spin fa-fw" /> Processing...';
    this.loadingIconDiv.className = 'pdf-loading';
    this.loadingIconDiv.appendChild(this.spinner);
    this.div.appendChild(this.loadingIconDiv);
  }

  update(scale, rotation) {
    this.scale = scale || this.scale;

    if (typeof rotation !== 'undefined') {
      this.rotation = rotation;
    }

    const totalRotation = (this.rotation + this.pdfPageRotate) % 360;
    this.viewport = this.viewport.clone({
      scale: this.scale * CSS_UNITS,
      rotation: totalRotation,
    });

    let isScalingRestricted = false;
    if (this.canvas && pdf.maxCanvasPixels > 0) {
      const {outputScale} = this;
      if (
        /* eslint-disable no-bitwise */
        ((Math.floor(this.viewport.width) * outputScale.sx) | 0) *
          ((Math.floor(this.viewport.height) * outputScale.sy) | 0) >
        pdf.maxCanvasPixels
        /* eslint-enable no-bitwise */
      ) {
        isScalingRestricted = true;
      }
    }

    if (this.canvas) {
      if (
        pdf.useOnlyCssZoom ||
        (this.hasRestrictedScaling && isScalingRestricted)
      ) {
        this.cssTransform(this.canvas, true);

        this.eventBus.dispatch('pagerendered', {
          source: this,
          pageNumber: this.id,
          cssTransform: true,
        });
        return;
      }
      if (!this.zoomLayer) {
        this.zoomLayer = this.canvas.parentNode;
        this.zoomLayer.style.position = 'absolute';
      }
    }
    if (this.zoomLayer) {
      this.cssTransform(this.zoomLayer.firstChild);
    }
    this.reset(true, true);
  }

  /**
   * Called when moved in the parent's container.
   */
  updatePosition() {
    if (this.textLayer) {
      this.textLayer.render(TEXT_LAYER_RENDER_DELAY);
    }
  }

  /* eslint-disable no-param-reassign */
  cssTransform(canvas, rerenderTC) {
    let scaleX = 1;
    let scaleY = 1;

    // Scale canvas, canvas wrapper, and page container.
    const {width, height} = this.viewport;
    const {div} = this;

    div.style.width = `${Math.floor(width)}px`;
    canvas.parentNode.style.width = div.style.width;
    canvas.style.width = div.style.width;
    div.style.height = `${Math.floor(height)}px`;
    canvas.parentNode.style.height = div.style.height;
    canvas.style.height = div.style.height;

    // The canvas may have been originally rotated, rotate relative to that.
    const relativeRotation = this.viewport.rotation - canvas._viewport.rotation;
    const absRotation = Math.abs(relativeRotation);
    if (absRotation === 90 || absRotation === 270) {
      // Scale x and y because of the rotation.
      scaleX = height / width;
      scaleY = width / height;
    }

    canvas.style.transform = `rotate(${relativeRotation}deg) scale(${scaleX},${scaleY})`;

    if (this.textLayer) {
      // Rotating the text layer is more complicated since the divs inside the
      // the text layer are rotated.
      // TODO: This could probably be simplified by drawing the text layer in
      // one orientation then rotating overall.
      const textLayerViewport = this.textLayer.viewport;
      const textRelativeRotation =
        this.viewport.rotation - textLayerViewport.rotation;
      const textAbsRotation = Math.abs(textRelativeRotation);
      let scale = width / textLayerViewport.width;
      if (textAbsRotation === 90 || textAbsRotation === 270) {
        scale = width / textLayerViewport.height;
      }
      const {textLayerDiv} = this.textLayer;

      let transX;
      let transY;
      switch (textAbsRotation) {
        case 0:
          transX = 0;
          transY = 0;
          break;
        case 90:
          transX = 0;
          transY = `-${textLayerDiv.style.height}`;
          break;
        case 180:
          transX = `-${textLayerDiv.style.width}`;
          transY = `-${textLayerDiv.style.height}`;
          break;
        case 270:
          transX = `-${textLayerDiv.style.width}`;
          transY = 0;
          break;
        default:
          logger.error('Bad rotation value.');
          break;
      }
      textLayerDiv.style.transform = `rotate(${textAbsRotation}deg) scale(${scale}, ${scale}) translate(${transX}, ${transY})`;
      textLayerDiv.style.transformOrigin = '0% 0%';
    }
    if (rerenderTC && this.TCLayer) {
      logger.warn('Rendering without rotation.');
      this.TCLayer.render(this.viewport, this.id);
    }
  }

  /* eslint-enable no-param-reassign */

  width() {
    return this.viewport.width;
  }

  height() {
    return this.viewport.height;
  }

  getPagePoint(x, y) {
    return this.viewport.convertToPdfPoint(x, y);
  }

  /**
   *  Approximates float number as a fraction using Farey sequence (max order
   *  of 8).
   *  @param {number} x - Positive float number.
   *  @returns {Array} Estimated fraction: the first array item is a numerator,
   *                   the second one is a denominator.
   */
  static approximateFraction(x) {
    let result;

    // a/b and c/d are neighbours in Farey sequence.
    let a = 0;
    let b = 1;
    let c = 1;
    let d = 1;

    // Fast paths for int numbers or their inversions.
    if (Math.floor(x) === x) {
      return [x, 1];
    }
    const xInv = 1 / x;
    const limit = 8;
    if (xInv > limit) {
      return [1, limit];
    }
    if (Math.floor(xInv) === xInv) {
      return [1, xInv];
    }

    let p;
    let q;
    const x_ = x > 1 ? xInv : x;

    // Limiting search to order 8.
    /* eslint-disable no-constant-condition */
    while (true) {
      // Generating next term in sequence (order of q).
      p = a + c;
      q = b + d;
      if (q > limit) {
        break;
      }
      if (x_ <= p / q) {
        c = p;
        d = q;
      } else {
        a = p;
        b = q;
      }
    }
    /* eslint-enable no-constant-condition */
    // Select closest of the neighbours to x.
    if (x_ - a / b < c / d - x_) {
      result = x_ === x ? [a, b] : [b, a];
    } else {
      result = x_ === x ? [c, d] : [d, c];
    }
    return result;
  }

  draw() {
    const self = this;
    let isCanvasHidden = true;
    let textLayer = null;

    if (this.renderingState !== RenderingStates.INITIAL) {
      throw new Error('Must be in new state before drawing');
    }

    this.renderingState = RenderingStates.RUNNING;

    const {div, pdfPage, viewport} = this;

    // Wrap the canvas so if it has a css transform for high dpi the overflow
    // will be hidden in FF.
    const canvasWrapper = document.createElement('div');
    canvasWrapper.style.width = div.style.width;
    canvasWrapper.style.height = div.style.height;
    canvasWrapper.classList.add('canvasWrapper');

    const canvas = document.createElement('canvas');
    canvas.id = `page${this.id}`;

    // Keep the canvas hidden until the first draw callback, or until drawing
    // is complete when `!this.renderingQueue`, to prevent black flickering.
    canvas.setAttribute('hidden', 'true');

    canvasWrapper.appendChild(canvas);
    if (this.TCLayer && this.TCLayer.div) {
      // TCLayer needs to stay on top
      div.insertBefore(canvasWrapper, this.TCLayer.div);
    } else {
      div.appendChild(canvasWrapper);
    }
    this.canvas = canvas;

    const ctx = canvas.getContext('2d', {alpha: false});
    const outputScale = PDFPageView.getOutputScale(ctx);

    this.outputScale = outputScale;

    if (pdf.useOnlyCssZoom) {
      const actualSizeViewport = viewport.clone({scale: CSS_UNITS});

      // Use a scale that will make the canvas be the original intended size
      // of the page.
      outputScale.sx *= actualSizeViewport.width / viewport.width;
      outputScale.sy *= actualSizeViewport.height / viewport.height;
      outputScale.scaled = true;
    }

    if (pdf.maxCanvasPixels > 0) {
      const pixelsInViewport = viewport.width * viewport.height;
      const maxScale = Math.sqrt(pdf.maxCanvasPixels / pixelsInViewport);

      if (outputScale.sx > maxScale || outputScale.sy > maxScale) {
        outputScale.sx = maxScale;
        outputScale.sy = maxScale;
        outputScale.scaled = true;
        this.hasRestrictedScaling = true;
      } else {
        this.hasRestrictedScaling = false;
      }
    }

    const sfx = PDFPageView.approximateFraction(outputScale.sx);
    const sfy = PDFPageView.approximateFraction(outputScale.sy);

    canvas.width = roundToDivide(viewport.width * outputScale.sx, sfx[0]);
    canvas.height = roundToDivide(viewport.height * outputScale.sy, sfy[0]);
    canvas.style.width = `${roundToDivide(viewport.width, sfx[1])}px`;
    canvas.style.height = `${roundToDivide(viewport.height, sfy[1])}px`;

    // Add the viewport so it's known what it was originally drawn with.
    canvas._viewport = viewport;

    if (this.textLayerFactory) {
      const textLayerDiv = document.createElement('div');
      textLayerDiv.className = 'textLayer';
      textLayerDiv.style.width = canvasWrapper.style.width;
      textLayerDiv.style.height = canvasWrapper.style.height;
      if (this.TCLayer && this.TCLayer.div) {
        // TCLayer needs to stay on top
        div.insertBefore(textLayerDiv, this.TCLayer.div);
      } else {
        div.appendChild(textLayerDiv);
      }

      textLayer = this.textLayerFactory.createTextLayerBuilder(
        textLayerDiv,
        this.id - 1,
        this.viewport
      );
    }

    if (this.TCLayerBuilder) {
      if (!this.TCLayer) {
        this.TCLayer = this.TCLayerBuilder.createTCLayerBuilder(
          div,
          this.pdfPage
        );
      }
      this.TCLayer.render(this.viewport, this.id, this.rotation);
    }

    this.textLayer = textLayer;

    let resolveRenderPromise;
    let rejectRenderPromise;
    const promise = new Promise((resolve, reject) => {
      resolveRenderPromise = resolve;
      rejectRenderPromise = reject;
    });

    let renderTask;

    // Rendering area
    function pageViewDrawCallback(error) {
      // The renderTask may have been replaced by a new one, so only remove
      // the reference to the renderTask if it matches the one that is
      // triggering this callback.
      /* eslint-disable no-use-before-define */
      if (renderTask === self.renderTask) {
        self.renderTask = null;
      }

      if (error === 'cancelled') {
        rejectRenderPromise(error);
        return;
      }

      self.renderingState = RenderingStates.FINISHED;

      if (isCanvasHidden) {
        self.canvas.removeAttribute('hidden');
        isCanvasHidden = false;
      }

      if (self.loadingIconDiv) {
        self.div.removeChild(self.loadingIconDiv);
        delete self.loadingIconDiv;
      }

      if (self.zoomLayer) {
        // Zeroing the width and height causes Firefox to release graphics
        // resources immediately, which can greatly reduce memory consumption.
        const zoomLayerCanvas = self.zoomLayer.firstChild;
        zoomLayerCanvas.width = 0;
        zoomLayerCanvas.height = 0;

        if (div.contains(self.zoomLayer)) {
          // Prevent "Node was not found" errors if the `zoomLayer` was
          // already removed. This may occur intermittently if the scale
          // changes many times in very quick succession.
          div.removeChild(self.zoomLayer);
        }
        self.zoomLayer = null;
      }

      self.error = error;
      self.stats = pdfPage.stats;
      if (self.onAfterDraw) {
        self.onAfterDraw();
      }
      self.eventBus.dispatch('pagerendered', {
        source: self,
        pageNumber: self.id,
        cssTransform: false,
      });

      if (!error) {
        resolveRenderPromise(undefined);
      } else {
        rejectRenderPromise(error);
      }
    }

    let renderContinueCallback = null;
    if (this.renderingQueue) {
      renderContinueCallback = (cont) => {
        if (!this.renderingQueue.isHighestPriority(this)) {
          this.renderingState = RenderingStates.PAUSED;
          this.resume = () => {
            this.renderingState = RenderingStates.RUNNING;
            cont();
          };
          return;
        }
        if (isCanvasHidden) {
          this.canvas.removeAttribute('hidden');
          isCanvasHidden = false;
        }
        cont();
      };
    }

    const transform = !outputScale.scaled
      ? null
      : [outputScale.sx, 0, 0, outputScale.sy, 0, 0];
    const renderContext = {
      canvasContext: ctx,
      transform,
      viewport: this.viewport,

      // intent: 'default', // === 'display'
    };
    this.renderTask = this.pdfPage.render(renderContext);
    renderTask = this.renderTask;
    renderTask.onContinue = renderContinueCallback;

    this.renderTask.promise.then(
      () => {
        pageViewDrawCallback(null);
        if (textLayer) {
          this.pdfPage
            .getTextContent({normalizeWhitespace: true})
            .then((textContent) => {
              textLayer.setTextContent(textContent);
              textLayer.render(TEXT_LAYER_RENDER_DELAY);
            });
        }
      },
      (error) => {
        pageViewDrawCallback(error);
      }
    );

    if (this.TCLayerBuilder) {
      if (!this.TCLayer) {
        this.TCLayer = this.TCLayerBuilder.createTCLayerBuilder(
          div,
          this.pdfPage
        );
      }
      this.TCLayer.render(this.viewport, this.id, this.rotation);
    }
    div.setAttribute('data-loaded', true);

    if (this.onBeforeDraw) {
      this.onBeforeDraw();
    }
    return promise;
  }
}

export default PDFPageView;
